import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RateReviewIcon from "@mui/icons-material/RateReview";
import Notes from "./header";
import FooterManual from "./footerManual";

const StyledCard = styled(Paper)(({ theme }) => ({
  height: "25vh",
  cursor: "pointer",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
  borderRadius: "10px",
  backgroundColor: "#fff",
  border: "2px solid #ebebeb",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-4px)",
    borderColor: "#EB2329",
  },
  [theme.breakpoints.down("sm")]: {
    height: "22vh",
    padding: theme.spacing(1.5),
  },
}));

const AccountCard = ({ icon, label, onClick, isIconComponent }) => (
  <StyledCard onClick={onClick}>
    {isIconComponent ? (
      React.createElement(icon, {
        sx: {
          fontSize: { xs: "40px", sm: "50px", md: "60px" },
          color: "#EB2329",
        },
      })
    ) : (
      <Box
        component="img"
        src={icon}
        alt={label}
        sx={{
          width: { xs: "40px", sm: "50px", md: "60px" },
          height: "auto",
          maxWidth: "100%",
          objectFit: "contain",
        }}
      />
    )}
    <Typography
      sx={{
        mt: 2,
        fontSize: { xs: "13px", sm: "15px", md: "16px" },
        fontWeight: 600,
        textAlign: "center",
        color: "#333",
      }}
    >
      {label}
    </Typography>
    <Button
      variant="outlined"
      sx={{
        border: "none",
        backgroundColor: "transparent",
        color: "#EB2329",
        textTransform: "none",
        fontSize: "12px",
        "&:hover": {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      View
    </Button>
  </StyledCard>
);

const MyAccount = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  const cards = [
    { label: "My Profile", icon: "/image/Account.svg", route: "/myAccount/myProfile", isIconComponent: false },
    {
      label: "Order History",
      icon: "/image/Buying.svg",
      route: "/myAccount/orderHistory",
      isIconComponent: false,
    },
    {
      label: "Inquiry History",
      icon: "/image/Inquiry.svg",
      route: "/myAccount/inquiryHistory",
      isIconComponent: false,
    },
    { label: "My Addresses", icon: "/image/address.svg", route: "/myAccount/myAddress", isIconComponent: false },
    { label: "Wishlist", icon: "/image/wishlist.svg", route: "/myAccount/wishList", isIconComponent: false },
    { label: "Performa Invoices", icon: "/image/Invoice.svg", route: "/myAccount/userPerformaInvoices", isIconComponent: false },
    { label: "Review & Rating", icon: "/image/Review.svg", route: "/myAccount/userReview", isIconComponent: false },
    { label: "Ticket", icon: "/image/Contact-us.svg", route: "/myAccount/ticketHistory", isIconComponent: false },
  ];

  const handleCardClick = (route) => {
    navigate(route);
  };

  const handleMyAccountClick = () => {
    navigate("/myAccount");
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Notes />

      {/* Hero Banner */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "30vh", sm: "40vh", md: "50vh" },
          backgroundColor: "black",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src="/image/shutterstock.png"
          alt="banner"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.57,
          }}
        />

        {/* Breadcrumb */}
        <Box
          sx={{
            position: "absolute",
            top: { xs: "5%", sm: "5%", md: "5%" },
            left: { xs: "3%", sm: "5%", md: "5%" },
            color: "white",
            fontSize: { xs: "11px", sm: "13px", md: "15px" },
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            padding: { xs: "4px 8px", sm: "5px 10px", md: "5px 10px" },
            borderRadius: "5px",
            display: "flex",
            gap: { xs: "2px", sm: "5px", md: "5px" },
          }}
        >
          <Link
            to="/"
            style={{
              fontSize: "inherit",
              color: "white",
              textDecoration: "none",
              opacity: 0.7,
            }}
          >
            Home
          </Link>
          <span style={{ opacity: 0.7 }}>/</span>
          <span>My Account</span>
        </Box>

        {/* Title with Button */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "white",
            fontSize: { xs: "20px", sm: "25px", md: "30px" },
            textAlign: "center",
            fontWeight: 700,
            textShadow: "2px 2px 8px rgba(0, 0, 0, 0.5)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: "10px", sm: "15px", md: "20px" },
            width: "90%",
          }}
        >
          My Account
        </Box>
      </Box>

      {/* Main Content Container */}
      <Container
        maxWidth="xl"
        sx={{
          width: "90%",
          backgroundColor: "#FFFFFF",
          border: "2px solid #EBEBEB",
          borderRadius: "10px",
          marginTop: "-75px",
          marginBottom: "100px",
          position: "relative",
          zIndex: 1,
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Cards Grid */}
        <Grid container spacing={2.5}>
          {cards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <AccountCard
                icon={card.icon}
                label={card.label}
                onClick={() => handleCardClick(card.route)}
                isIconComponent={card.isIconComponent}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ mt: { xs: "20%", sm: "15%", md: "10%", lg: "5%" } }}>
        <FooterManual />
      </Box>
    </Box>
  );
};

export default MyAccount;